import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ProfileDropDown from "units/dashboard/components/ProfileDropDown";
import { useSidebar } from 'units/dashboard/context/SidebarProvider';
import { getUserType } from '../services/getUserProfile';
import { getknowlageBaseInfo } from '../../user/services/knowlageBaseInfo';
import { Wallet } from 'lucide-react';
import Notification from "./Notification";
import { MdGridView } from "react-icons/md";
import { getBotStatus } from 'units/bot/services/botService';
import { AiFillFund } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';

const Header = () => {
    const { t, i18n } = useTranslation(["user"]);

    const { toggleSidebar } = useSidebar();
    const [isHidden, setIsHidden] = useState(false);
    const [userType, setUserType] = useState(null);
    const [wallet, setWallet] = useState(0);
    const [income, setIncome] = useState(0);
    const accessToken = useSelector((state) => state.auth.accessToken);
    const [users, setUsers] = useState(0);
    const [userLimit, setUserLimit] = useState(0);
    const [remainingTokens, setRemainingTokens] = useState("");
    const [plan, setPlan] = useState("");
    const [isPlanActive, setIsPlanActive] = useState(false);
    const user = useSelector((state) => state.userInfo.username);
    const isEnglish = (i18n.language === "en");

    useEffect(() => {
        if (!user) return;
        const init = async () => {
            try {
                const res = await getBotStatus(user);
                setIsPlanActive(res?.status === "active");
            } catch (err) {
                console.error("getBotStatus failed:", err);
                setIsPlanActive(false);
            }
        };
        init();
    }, [user]);

    useEffect(() => {
        const handleResize = () => setIsHidden(window.innerWidth >= 1139);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!accessToken) return;
        const fetchUser = async () => {
            try {
                const res = await getUserType(accessToken);
                if (res?.ok && res?.data) setUserType(res?.data?.userType);
            } catch (error) {
                console.error("خطا در دریافت اطلاعات کاربر:", error);
            }
        };
        fetchUser();
    }, [accessToken]);

    useEffect(() => {
        if (!accessToken) return;
        const getUser = async () => {
            const res = await getknowlageBaseInfo(accessToken);
            if (res?.ok) {
                setWallet(res.data.wallet);
                setUserLimit(res.data.plan?.userLimit);
                setIncome(res.data.pendingSettlement);
                setUsers(res.data.users);
                setRemainingTokens(res.data.subscription?.remainingTokens);
                setPlan(res.data.plan?.isBase);
            }
        };
        getUser();
    }, [accessToken]);

    return (
        <header
            className="
                relative flex items-center justify-between w-full z-50
                backdrop-blur-xl
                bg-gradient-to-l from-[#0F3B3C]/95 via-[#134E4F]/95 to-[#0F3B3C]/95
                dark:!from-slate-900/95 dark:!via-[#0B2E2F]/95 dark:!to-slate-900/95
                border-b border-teal-500/30 dark:!border-teal-400/20
                shadow-[0_4px_30px_-8px_rgba(15,118,110,0.5)]
                dark:!shadow-[0_4px_30px_-8px_rgba(0,0,0,0.7)]
                before:content-[''] before:absolute before:inset-x-0 before:bottom-0 before:h-[2px]
                before:bg-gradient-to-l before:from-transparent before:via-amber-400/80 before:to-transparent
            "
        >
            {/* Brand + Menu toggle */}
            <div className="topbar-brand flex items-center shrink-0">
                <div className="navbar-brand flex items-center justify-between shrink-0 h-[70px] border-r border-teal-400/20 dark:!border-teal-400/10">
                    <a href="#" className="group flex items-center font-bold text-lg transition-transform duration-300 hover:scale-[1.02]">
                        <img
                            src='/mainLogo.png'
                            className="ltr:mr-1 rtl:ml-1 hidden sm:block h-[60px] object-contain w-auto my-1 mx-2 drop-shadow-[0_2px_8px_rgba(251,191,36,0.25)] transition-all duration-500 group-hover:drop-shadow-[0_2px_14px_rgba(251,191,36,0.55)]"
                            alt={t("header.logo")}
                        />
                        <img
                            src='/logo_raya_ho.png'
                            className="block sm:hidden h-[60px] object-contain my-1 mx-2 drop-shadow-[0_2px_8px_rgba(251,191,36,0.3)]"
                            alt={t("header.logo")}
                        />
                    </a>
                </div>

                <button
                    type="button"
                    className={`
                        menuBtn relative mx-3 w-5 h-5 p-5
                        flex items-center justify-center rounded-xl
                        text-amber-200 hover:text-amber-100
                        bg-transparent
                        backdrop-blur-md
                        transition-all duration-300 ease-out
                        hover:scale-105 active:scale-95
                        hover:shadow-[0_0_20px_-4px_rgba(251,191,36,0.5)]
                        dark:!text-amber-300 dark:!hover:text-amber-200
                        ${isHidden ? 'hidden' : ''}
                    `}
                    id="vertical-menu-btn"
                    onClick={toggleSidebar}
                    aria-label={t("header.toggleSidebar")}
                >
                    <i className="fa fa-fw fa-bars text-lg"></i>
                </button>
            </div>

            {/* Right side info */}
            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 px-2 sm:px-4 min-w-0">

                {/* 👈 چیپ‌ها فقط از sm به بالا — روی موبایل داخل ProfileDropDown نشون داده می‌شن */}
                {userType == 1 && (
                    <div className={`hidden sm:flex items-center gap-1.5 sm:gap-2 md:gap-3 ${isEnglish && 'font-sans'}`}>
                        {/* Income chip */}
                        <div
                            className="
                                group relative inline-flex items-center gap-1 sm:gap-2
                                rounded-xl px-1.5 sm:px-2.5 md:px-3 py-1.5 sm:py-2
                                text-[10px] sm:text-[11px] md:text-xs font-semibold whitespace-nowrap
                                bg-gradient-to-l from-amber-400/15 to-amber-300/5
                                text-amber-100
                                border border-amber-300/40
                                backdrop-blur-md
                                shadow-[0_4px_20px_-8px_rgba(251,191,36,0.5)]
                                transition-all duration-300
                                hover:scale-[1.03] hover:border-amber-200/70
                                hover:shadow-[0_6px_24px_-6px_rgba(251,191,36,0.7)]
                                dark:!from-amber-400/10 dark:!to-amber-300/5
                                dark:!text-amber-200 dark:!border-amber-300/30
                            "
                        >
                            <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-amber-400/0 via-amber-300/10 to-amber-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <AiFillFund className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-amber-300 shrink-0" />
                            <span className=" opacity-80">{t("header.income")}</span>
                            <span className="text-amber-200 font-bold">{income.toLocaleString('en-US')}</span>
                            <span className=" opacity-70">{t("header.currency")}</span>
                        </div>

                        {/* Wallet chip */}
                        <div
                            className="
                                group relative inline-flex items-center gap-1 sm:gap-2
                                rounded-xl px-1.5 sm:px-2.5 md:px-3 py-1.5 sm:py-2
                                text-[10px] sm:text-[11px] md:text-xs font-semibold whitespace-nowrap
                                bg-gradient-to-l from-teal-400/15 to-teal-300/5
                                text-teal-50
                                border border-teal-300/40
                                backdrop-blur-md
                                shadow-[0_4px_20px_-8px_rgba(45,212,191,0.5)]
                                transition-all duration-300
                                hover:scale-[1.03] hover:border-teal-200/70
                                hover:shadow-[0_6px_24px_-6px_rgba(45,212,191,0.7)]
                                dark:!from-teal-400/10 dark:!to-teal-300/5
                                dark:!text-teal-100 dark:!border-teal-300/30
                            "

                        >
                            <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-teal-400/0 via-teal-300/10 to-teal-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <Wallet className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-teal-300 shrink-0" />
                            <span className=" opacity-70">{t("header.credit")}</span>
                            <span className="text-teal-100 font-bold">{wallet.toLocaleString('en-US')}</span>
                            <span className=" opacity-70">{t("header.currency")}</span>
                        </div>

                        {/* Plan status chip */}
                        <div
                            className={`
                                group relative inline-flex items-center gap-1 sm:gap-2
                                rounded-xl px-1.5 sm:px-2.5 md:px-3 py-1.5 sm:py-2
                                text-[10px] sm:text-[11px] md:text-xs font-semibold whitespace-nowrap
                                border backdrop-blur-md
                                transition-all duration-300
                                hover:scale-[1.03]
                                ${isPlanActive
                                    ? "bg-gradient-to-l from-emerald-400/20 to-emerald-300/5 text-emerald-50 border-emerald-300/50 shadow-[0_4px_20px_-8px_rgba(16,185,129,0.6)] hover:shadow-[0_6px_24px_-6px_rgba(16,185,129,0.8)] dark:!border-emerald-300/40"
                                    : "bg-gradient-to-l from-rose-500/20 to-rose-400/5 text-rose-50 border-rose-400/50 shadow-[0_4px_20px_-8px_rgba(244,63,94,0.6)] hover:shadow-[0_6px_24px_-6px_rgba(244,63,94,0.8)] dark:!border-rose-400/40"
                                }
                            `}
                        >
                            <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2 shrink-0">
                                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isPlanActive ? "bg-emerald-300" : "bg-rose-300"}`} />
                                <span className={`relative inline-flex rounded-full h-full w-full ${isPlanActive ? "bg-emerald-300" : "bg-rose-300"}`} />
                            </span>
                            <MdGridView size={14} className="hidden sm:block opacity-90 shrink-0" />
                            <span className="opacity-90">{t("header.planStatus")}</span>
                            <span className="font-bold">
                                {isPlanActive ? t("header.active") : t("header.inactive")}
                            </span>
                        </div>
                    </div>
                )}

                {/* <Notification users={users} userLimit={userLimit} remainingTokens={remainingTokens} plan={plan} /> */}

                {/* Profile Dropdown wrapper — همه‌چیز موبایل اینجاست */}
                <div className="dropdown relative shrink-0">
                    <div
                        className="
                            rounded-xl p-[1px]
                            bg-gradient-to-br from-amber-300/60 via-teal-300/40 to-amber-300/60
                            hover:from-amber-300/90 hover:via-teal-200/60 hover:to-amber-300/90
                            transition-all duration-500
                            shadow-[0_4px_20px_-8px_rgba(45,212,191,0.5)]
                            hover:shadow-[0_6px_24px_-6px_rgba(251,191,36,0.6)]
                        "
                    >
                        <div className="rounded-[11px] bg-[#0F3B3C]/90 dark:!bg-slate-900/90 backdrop-blur-md">
                            <ProfileDropDown
                                userType={userType}
                                wallet={wallet}
                                income={income}
                                isPlanActive={isPlanActive}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Decorative corner glow */}
            <div className="pointer-events-none absolute -top-6 right-1/4 h-16 w-40 rounded-full bg-amber-400/10 blur-3xl dark:!bg-amber-400/5" />
            <div className="pointer-events-none absolute -bottom-6 left-1/3 h-16 w-40 rounded-full bg-teal-400/10 blur-3xl dark:!bg-teal-400/5" />
        </header>
    );
};

export default Header;